import { useMemo } from 'react';
import { formatDate } from '@carbonify/ui/utils/format';
import { Block } from 'components/Blocks';
import AuthorBlock from 'components/Blocks/AuthorBlock';
import BlogSocialLinks from 'components/BlogSocialLinks/BlogSocialLinks';
import Hero from 'components/Hero/Hero';
import Layout from 'components/Layout/Layout';
import ModuleItem from 'components/Modules';
import GenericModule from 'components/Modules/GenericModule';
import RichContent from 'components/RichContent/RichContent';
import dynamic from 'next/dynamic';
import { fetchAllPostSlugs, fetchBlogPost, fetchLatestBlogPosts } from 'services/blog/ssr';
import useConfig from 'services/config';
import { fetchBrandConfig } from 'services/config/ssr';
import mergeStaticProps, { StaticProps } from 'utils/mergeStaticProps';

const SocialLinks = dynamic(() => Promise.resolve(BlogSocialLinks), {
  ssr: false,
});

export default function BlogPostPage({ blogPost, ...rest }: StaticProps<typeof fetchBlogPost>) {
  const { latestPosts } = rest as unknown as StaticProps<typeof fetchLatestBlogPosts>;
  const { locale } = useConfig();
  const latestPostsContent = useMemo(
    () =>
      latestPosts?.map<Block>(({ id, title, titleImage, slug, lead, author }) => ({
        __typename: 'BlogPostBlockRecord',
        image: titleImage,
        id,
        title,
        slug,
        description: lead,
        author,
      })) || [],
    [latestPosts],
  );

  return (
    <Layout theme={blogPost.titleImage ? 'light' : 'dark'}>
      <Hero
        title={`**${blogPost.title}**`}
        variant="blog_post"
        imageDesktop={blogPost.titleImage}
        theme={blogPost.titleImage ? 'light' : 'dark'}
      />
      <article className="mx-auto my-16 w-full max-w-[50rem] px-5">
        {blogPost.lead && <RichContent as="div">{`###### ${blogPost.lead}`}</RichContent>}
        {blogPost.author?.title && (
          <AuthorBlock
            {...blogPost.author}
            withMargins
            subtitle={blogPost._publishedAt && formatDate(blogPost._publishedAt, locale)}
          />
        )}
        {blogPost.blogPostContent && (
          <RichContent
            as="div"
            disableContentLink
          >
            {blogPost.blogPostContent}
          </RichContent>
        )}
      </article>
      <SocialLinks />
      {blogPost.blogSections?.length ? blogPost.blogSections.map(ModuleItem) : null}
      {latestPostsContent.length && (
        <GenericModule
          id="latest-blog-posts"
          __typename="GenericModuleRecord"
          variant="card_slider_section"
          title=""
          subtitle=""
          offset={0}
          theme="light"
          content={latestPostsContent}
        />
      )}
    </Layout>
  );
}

export const getStaticProps = mergeStaticProps(fetchBrandConfig, fetchLatestBlogPosts, fetchBlogPost);

export const getStaticPaths = fetchAllPostSlugs;
